import { useEffect, useRef } from 'react'
import {
  DOMAIN_LABEL,
  HOUSEHOLD_FACT_REGISTRY,
  RULE_SOURCE_LABEL,
  STATUS_LABEL,
  type Assessment,
  type Finding,
} from '@asvise/household-engine/vocabulary'
import { DOMAIN, FINDING, STATUS, UI } from '../lib/language'

/**
 * What we found, in the five parts the framework asks for.
 *
 * What we found, why it matters to this household, what evidence was used, what
 * remains uncertain, and what can be done next. The engine emits all five; this
 * arranges them.
 *
 * Three rules about what may be shown, and each is a thing the product has
 * already promised in writing.
 *
 * A BAND IS A READING ORDER, NOT AN INSTRUCTION. Asvise's privacy policy says
 * the platform does not make a decision that produces legal or similarly
 * significant effects, and its adviser terms call words like "gap" and
 * "comparison" organisational indicators only. So "Review now" is printed as
 * where to start, the tie-break is disclosed, and nothing here says buy, cancel
 * or switch. That is on the board, where the bands are. The card is about one
 * area and carries neither the band nor the sentence that explains it.
 *
 * THE TWO VIEWS SIT SIDE BY SIDE AND ARE NEVER AVERAGED. What this household
 * calculated is one number; what the public guidance says is another. Showing
 * the second as though it were the first is the risk the framework names, and
 * the labels say which is which.
 *
 * A TOTAL NOBODY CAN CHECK IS AN ASSERTION. Every component of a calculation is
 * printed with the answer it came from, because a household cannot disagree
 * with a number it cannot take apart.
 */

/**
 * A figure, in the currency this product is configured for.
 *
 * Written out rather than left to `style: 'currency'`, which for en-SG narrows
 * SGD to a bare "$". That put "$100,000" beside a quoted schedule line reading
 * "S$100,000" on the same card, and a bare dollar sign in a Singapore product
 * is not a shorter way of saying the same thing: it is the symbol the reader
 * will take for US dollars.
 */
const money = (n: number) => `S$${Math.round(n).toLocaleString('en-SG')}`

const TONE: Record<string, string> = {
  ATTENTION_REQUIRED: 'attention',
  PARTIALLY_ADDRESSED: 'partial',
  NO_OBVIOUS_GAP: 'clear',
  NOT_APPLICABLE: 'clear',
}

/**
 * One area in full, over the board rather than instead of it.
 *
 * This was a screen listing every area, opened at the one that was clicked and
 * scrolled to it, and the argument for that was real: reading one area's
 * evidence beside another's is part of what makes a ranking checkable rather
 * than asserted. What it cost was the board. Somebody following a row left the
 * six areas and their order behind, read eleven cards, and came back by a
 * button at the top.
 *
 * So it is a sheet, which is where answering already happens: the board stays
 * mounted underneath, dismissing returns to the exact row and scroll position,
 * and focus trapping, Esc and the backdrop come with the native dialog. The
 * order is still legible, because it is on the screen behind this one.
 *
 * THE SCREENING-LEVEL PRINT COMES WITH IT. The disclaimer, the date, the rule
 * set version and the areas this rule set does not screen were at the foot of
 * that page, and they are framework section 10.1's version disclosure rather
 * than decoration: a verdict somebody is reading is the moment to say which
 * rules produced it. They are at the foot of the sheet for that reason.
 */
export function FindingSheet({
  assessment,
  finding,
  onClose,
}: {
  assessment: Assessment
  finding: Finding
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const t = (key: keyof typeof UI) => UI[key] ?? key

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  return (
    <dialog
      ref={ref}
      className="sheet finding-sheet"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close()
      }}
    >
      <div className="sheet-body">
        <header className="answering-head">
          <span className="answering-where">{t('whatWeFound')}</span>
          <button
            type="button"
            className="sheet-close"
            onClick={() => ref.current?.close()}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <Area finding={finding} assessment={assessment} />

        {assessment.out_of_scope.length > 0 && (
          <p className="uncertain">
            {t('notLookedAt')}:{' '}
            {assessment.out_of_scope
              .map((d) => (DOMAIN[d] ?? DOMAIN_LABEL[d]).toLowerCase())
              .join(', ')}
            . {t('aboutScreening')}
          </p>
        )}

        <p className="disclaimer">
          {assessment.disclaimer} {t('screenedOn')}{' '}
          {new Date(assessment.evaluated_at).toLocaleDateString('en-SG', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}{' '}
          {t('againstRuleSet')} {assessment.rule_set_version}.
        </p>
      </div>
    </dialog>
  )
}

function Area({ finding, assessment }: { finding: Finding; assessment: Assessment }) {
  const t = (key: keyof typeof UI) => UI[key] ?? key
  const tone = TONE[finding.status] ?? 'unsettled'

  return (
    <section className={`area ${tone}`}>
      <div className="area-head">
        <h3>{DOMAIN[finding.domain] ?? DOMAIN_LABEL[finding.domain]}</h3>
        {finding.person_label && (
          <span className="area-who">
            {t('forPerson')} {finding.person_label}
          </span>
        )}
        {/*
          What was found about this area, and not where it sits. The card used to
          carry the band as well, "Review now" or "More information needed", which is a
          fact about the board: the position among the other areas is on the
          screen behind this one, with the order's own disclosure, and printing
          it again inside a card about one area made it read as an instruction
          to somebody who had just chosen to look at this one.
        */}
        <span className="area-status">
          {STATUS[finding.status] ?? STATUS_LABEL[finding.status]}
        </span>
      </div>

      <dl>
        <div>
          <dt>{t('whatWeFound')}</dt>
          <dd>
            {/*
              Said from the reason code rather than from the engine's own
              sentence, which carries figures interpolated in English word
              order. Where no translation exists the engine's English is shown,
              because a household reading a blank learns nothing.
            */}
            {FINDING[finding.reason_codes[0] ?? ''] ??
              finding.evidence[0]?.trigger ??
              'Nothing was established for this.'}
          </dd>
        </div>

        {(finding.need || finding.out_of_pocket || finding.missing.length > 0) && (
          <div>
            <dt>{t('whyItMatters')}</dt>
            <dd>
              {(finding.need || finding.missing.length > 0) && <Calculation finding={finding} />}
              {finding.out_of_pocket && (
                <p>
                  On a hospital bill of {money(finding.out_of_pocket.bill)}, this policy would leave
                  you to find {money(finding.out_of_pocket.household_pays)}: a deductible of{' '}
                  {money(finding.out_of_pocket.deductible)} and{' '}
                  {money(finding.out_of_pocket.co_insurance)} of co-insurance. That is an
                  illustration on a figure you chose, not a prediction.
                  {finding.out_of_pocket.savings_after !== null && (
                    <>
                      {' '}
                      It would leave {money(finding.out_of_pocket.savings_after)} of the savings you
                      told us about.
                    </>
                  )}
                </p>
              )}
            </dd>
          </div>
        )}

        {finding.cover.matches.length > 0 && (
          <div>
            <dt>{t('whatEvidence')}</dt>
            <dd>
              {finding.cover.matches.map((match, i) => (
                <p key={i} className="quote">
                  <span className="where">
                    {match.policy_label}
                    {match.benefit.citation?.page !== null &&
                      match.benefit.citation !== undefined &&
                      `, page ${match.benefit.citation.page}`}
                  </span>
                  {match.benefit.citation?.verbatim ?? 'No quoted line was recorded.'}
                </p>
              ))}
              {finding.interactions.map((interaction, i) => (
                <p key={`x${i}`}>
                  Paying the {readable(interaction.paid_code)} benefit of{' '}
                  {money(interaction.paid_amount)} would reduce{' '}
                  {interaction.reduces
                    .map((r) => `${readable(r.code)} from ${money(r.from)} to ${money(r.to)}`)
                    .join(', and ')}
                  . These are claims against one sum, so they cannot be added together.
                </p>
              ))}
            </dd>
          </div>
        )}

        <div>
          <dt>{t('whatUncertain')}</dt>
          <dd>
            {finding.uncertainties.length === 0 && finding.asks.length === 0 ? (
<p>{t('nothingUncertain')}</p>
            ) : (
              <ul className="uncertain">
                {[...finding.uncertainties, ...finding.asks].map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </dd>
        </div>

        <div>
          <dt>{t('whatNext')}</dt>
          <dd>
            <p>{assessment.affordability.permitted_guidance}</p>
            <p className="area-who">
              {t('decidedBy')} {finding.rule_ids.join(', ')} ·{' '}
              {RULE_SOURCE_LABEL[finding.evidence[0]?.rule_source ?? 'ASVISE_SCREENING']}
            </p>
          </dd>
        </div>
      </dl>
    </section>
  )
}

/**
 * The household's own calculation beside the public benchmark.
 *
 * Two figures answering one question differently, and the labels say which is
 * which. A benchmark read as a personal requirement is a named risk, and the
 * only thing that prevents it is never printing one without the other.
 */
function Calculation({ finding }: { finding: Finding }) {
  const t = (key: keyof typeof UI) => UI[key] ?? key
  const need = finding.need

  /**
   * Said from the finding, not the estimate. An incomplete calculation has its
   * figure withheld from the card, so `need` is null exactly when something is
   * missing, and this branch read the list off `need`: it could not be reached.
   * The card went straight from what was found to what remains uncertain, with
   * the engine's reason for having no figure written and never shown.
   *
   * The questions' own words rather than the field names, which are what the
   * rule's `asks` prints and which are not something a household has seen.
   */
  if (finding.missing.length > 0) {
    return (
      <p>
        We have not worked out an amount, because some answers are missing:{' '}
        {finding.missing.map((f) => HOUSEHOLD_FACT_REGISTRY[f].label.toLowerCase()).join(', ')}. A
        total built from part of the answers would be smaller than the truth, and this is the one
        direction we will not be wrong in.
      </p>
    )
  }

  if (!need || need.total === null) return null

  return (
    <>
      <table className="components">
        <tbody>
          {need.components.map((c, i) => (
            <tr key={i} className={c.reduces ? 'reduces' : undefined}>
              <td>
                {c.reduces ? 'less ' : ''}
                {c.label}
                {c.note && <span className="note">{c.note}</span>}
              </td>
              <td>{money(c.amount)}</td>
            </tr>
          ))}
          <tr className="total">
            <td>{t('scenarioTotal')}</td>
            <td>{money(need.total)}</td>
          </tr>
        </tbody>
      </table>

      <div className="views">
        <div className="view">
          <h4>{t('yourCalculation')}</h4>
          <p className="figure">{money(need.total)}</p>
          {finding.shortfall !== null && finding.shortfall > 0 && (
            <p>
              {typeof finding.verified_cover === 'number'
                ? `${money(finding.verified_cover)} of cover was identified, leaving ${money(finding.shortfall)} of this scenario unaddressed.`
                : `${money(finding.shortfall)} of this scenario is unaddressed.`}
            </p>
          )}
        </div>

        {finding.benchmark?.amount !== null && finding.benchmark !== null && (
          <div className="view">
            <h4>{t('ruleOfThumb')}</h4>
            <p className="figure">{money(finding.benchmark.amount!)}</p>
            <p>
              {finding.benchmark.label}, from the {finding.benchmark.authority}.{' '}
              {finding.benchmark.caveat}
            </p>
          </div>
        )}
      </div>
    </>
  )
}

const readable = (code: string): string =>
  ({
    DEATH: 'death',
    TPD: 'permanent disability',
    CRITICAL_ILLNESS: 'critical illness',
  })[code] ?? code.toLowerCase().replace(/_/g, ' ')
